import { useAuth } from '@/contexts/AuthContext';
import { useSubscription, useUserProfile } from '@/contexts/SubscriptionContext';
import { supabase } from '@/lib/supabase';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import {
  ArrowsClockwise,
  BookOpenText,
  CalendarCheck,
  ChartBar,
  Crown,
  FileText,
  Fire,
  GraduationCap,
  PencilSimple,
  ShieldCheck,
  SignOut,
  Star,
  Trash,
  Trophy,
  UserCircle,
  Warning
} from 'phosphor-react-native';
import { useState } from 'react';
import { Alert, Linking, Modal, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, ProgressBar, Text, TextInput } from 'react-native-paper';

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo200: '#C7D2FE',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
  amber600: '#D97706',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
  rose600: '#E11D48',
};

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 1200;

// Helper pour formater la date en français
function formatDateFr(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

// Helper pour obtenir le nom du niveau
function getLevelName(level: number): string {
  if (level <= 2) return 'Débutant';
  if (level <= 5) return 'Apprenti';
  if (level <= 10) return 'Confirmé';
  if (level <= 20) return 'Expert';
  return 'Maître';
}

export default function ProfileScreen() {
  const { profile, loading, refetch } = useUserProfile();
  const { session } = useAuth();
  const { 
    subscribed, 
    cancelAtPeriodEnd, 
    subscriptionEnd, 
    loading: subscriptionLoading,
    openCustomerPortal,
  } = useSubscription();

  const [isResettingDiploma, setIsResettingDiploma] = useState(false);
  const [showDiplomaModal, setShowDiplomaModal] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  
  // États pour la suppression de compte
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Calculer le niveau basé sur l'XP
  const xp = profile?.xp || 0;
  const currentLevel = Math.floor(xp / 100) + 1;
  const xpInCurrentLevel = xp % 100;
  const xpForNextLevel = 100;
  const progress = xpInCurrentLevel / xpForNextLevel;

  // Nombre de matières
  const subjectCount = profile?.subject_codes?.length || 0;

  // Date de création du compte
  const createdAt = session?.user?.created_at;

  const handleLogout = async () => {
    if (isWeb) {
      if (window.confirm('Es-tu sûr de vouloir te déconnecter ?')) {
        await supabase.auth.signOut();
        router.replace('/login');
      }
    } else {
      Alert.alert(
        'Déconnexion',
        'Es-tu sûr de vouloir te déconnecter ?',
        [
          { text: 'Annuler', style: 'cancel' },
          {
            text: 'Déconnecter',
            style: 'destructive',
            onPress: async () => {
              await supabase.auth.signOut();
              router.replace('/login');
            },
          },
        ]
      );
    }
  };

  const handleChangeDiploma = async () => {
    setShowDiplomaModal(false);
    setIsResettingDiploma(true);

    try {
      const userId = session?.user?.id;
      if (!userId) throw new Error('Utilisateur non connecté');

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          diploma: null,
          section: null,
          subject_codes: [],
          onboarding_completed: false,
        })
        .eq('id', userId);

      if (profileError) throw profileError;

      const { error: subjectsError } = await supabase
        .from('user_subjects')
        .delete()
        .eq('user_id', userId);

      if (subjectsError) throw subjectsError;

      router.replace('/onboarding?changeDiploma=true');
      
    } catch (error: any) {
      console.error('Error resetting diploma:', error);
      if (isWeb) {
        window.alert('Impossible de réinitialiser le diplôme. Réessaie.');
      } else {
        Alert.alert('Erreur', 'Impossible de réinitialiser le diplôme. Réessaie.');
      }
    } finally {
      setIsResettingDiploma(false);
    }
  };

  const handleModifySubjects = () => {
    router.push('/onboarding?edit=true');
  };

  const handleManageSubscription = async () => {
    setIsPortalLoading(true);
    try {
      await openCustomerPortal();
    } catch (error) {
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleUpgrade = () => {
    router.push('/paywall');
  };

  // ✅ Restaurer les achats (pour les anciens abonnés)
  const handleRestorePurchases = async () => {
    try {
      const { restorePurchases } = require('@/lib/revenuecat');
      const result = await restorePurchases();
      if (result.success && result.isPremium) {
        await supabase.from('profiles').update({ premium: true, subscription_source: result.store || 'apple' }).eq('id', session?.user?.id);
        refetch();
        if (isWeb) { window.alert('Achats restaurés ! Ton abonnement Premium est actif.'); }
        else { Alert.alert('✅ Achats restaurés', 'Ton abonnement Premium a été restauré !'); }
      } else {
        if (isWeb) { window.alert('Aucun abonnement trouvé pour ce compte Apple/Google.'); }
        else { Alert.alert('Aucun abonnement', 'Aucun abonnement actif trouvé pour ce compte Apple/Google.'); }
      }
    } catch (error) {
      console.error('Restore error:', error);
      if (isWeb) { window.alert('Erreur lors de la restauration. Réessaie.'); }
      else { Alert.alert('Erreur', 'Impossible de restaurer les achats. Réessaie.'); }
    }
  };

  const handleDeleteAccountPress = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteAccountContinue = () => {
    setShowDeleteModal(false);
    setShowDeleteConfirmModal(true);
    setDeleteConfirmText('');
  };

  const handleDeleteAccountConfirm = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') return;

    setIsDeleting(true);

    try {
      const userId = session?.user?.id;
      if (!userId) throw new Error('Utilisateur non connecté');

      // Supprimer les données utilisateur dans l'ordre (respecter les foreign keys)
      await supabase.from('feedbacks').delete().eq('user_id', userId);
      await supabase.from('quiz_results').delete().eq('user_id', userId);
      await supabase.from('flashcards').delete().eq('user_id', userId);
      await supabase.from('flashcard_sets').delete().eq('user_id', userId);
      await supabase.from('notes').delete().eq('user_id', userId);
      await supabase.from('documents').delete().eq('user_id', userId);
      await supabase.from('coach_conversations').delete().eq('user_id', userId);
      await supabase.from('user_subjects').delete().eq('user_id', userId);
      await supabase.from('user_activity').delete().eq('user_id', userId);
      await supabase.from('user_stats').delete().eq('user_id', userId);
      await supabase.from('generation_runs').delete().eq('user_id', userId);
      await supabase.from('one_time_purchases').delete().eq('user_id', userId);
      await supabase.from('subscriptions').delete().eq('user_id', userId);
      await supabase.from('quizzes').delete().eq('user_id', userId);
      await supabase.from('knowledge_bases').delete().eq('user_id', userId);

      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (profileError) {
        console.error('Error deleting profile:', profileError);
      }

      const { error: deleteError } = await supabase.functions.invoke('delete-user', {
        body: { userId },
      });

      if (deleteError) {
        console.error('Error deleting auth user:', deleteError);
      }

      setShowDeleteConfirmModal(false);
      setDeleteConfirmText('');

      await supabase.auth.signOut();
      
      if (isWeb) {
        window.alert('Ton compte a été supprimé avec succès. Nous sommes tristes de te voir partir !');
        router.replace('/login');
      } else {
        Alert.alert(
          'Compte supprimé',
          'Ton compte a été supprimé avec succès. Nous sommes tristes de te voir partir !',
          [{ text: 'OK', onPress: () => router.replace('/login') }]
        );
      }

    } catch (error: any) {
      console.error('Error deleting account:', error);
      if (isWeb) {
        window.alert('Une erreur est survenue lors de la suppression. Contacte le support si le problème persiste.');
      } else {
        Alert.alert(
          'Erreur',
          'Une erreur est survenue lors de la suppression. Contacte le support si le problème persiste.'
        );
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Handlers pour les liens légaux
  const handleOpenPrivacyPolicy = () => {
    Linking.openURL('https://www.ernest-app.fr/confidentialité');
  };

  const handleOpenTerms = () => {
    Linking.openURL('https://www.ernest-app.fr/cgu');
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={colors.indigo600} />
        <Text style={styles.loadingText}>Chargement...</Text>
      </View>
    );
  }

  const initial = profile?.full_name ? profile.full_name.charAt(0).toUpperCase() : 'U';

  return (
    <View style={styles.container}>
      {/* ✅ Header FIXE - En dehors du ScrollView */}
      <View style={styles.headerWrapper}>
        <LinearGradient
          colors={[colors.indigo500, colors.indigo600, colors.indigo700]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.header}
        >
          <View style={styles.headerInner}>
            <View style={styles.headerIconCircle}>
              <Text style={styles.headerAvatarText}>{initial}</Text>
            </View>
            <View style={styles.headerTextContainer}>
              <Text style={styles.headerTitle}>Mon Profil</Text>
              <Text style={styles.headerSubtitle}>Paramètres & statistiques</Text>
            </View>
          </View>
        </LinearGradient>
      </View>

      {/* ✅ Contenu SCROLLABLE */}
      <ScrollView 
        style={styles.scrollView} 
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.innerContainer}>
          {/* Carte utilisateur */}
          <View style={styles.section}>
            <View style={styles.userCard}>
              <View style={styles.userInfo}>
                <Text style={styles.userName}>{profile?.full_name || 'Utilisateur'}</Text>
                <Text style={styles.userEmail}>{session?.user?.email || ''}</Text>
                {createdAt && (
                  <View style={styles.memberSince}>
                    <UserCircle size={14} color={colors.slate400} weight="duotone" />
                    <Text style={styles.memberSinceText}>
                      Membre depuis {formatDateFr(createdAt)}
                    </Text>
                  </View>
                )}
              </View>
            </View>
          </View>

          {/* Mon parcours */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <GraduationCap size={20} color={colors.indigo600} weight="duotone" />
              <Text style={styles.sectionTitle}>Mon parcours</Text>
            </View>

            {/* Diplôme */}
            <View style={styles.settingCard}>
              <View style={styles.settingLeft}>
                <View style={[styles.settingIcon, { backgroundColor: colors.indigo100 }]}>
                  <GraduationCap size={20} color={colors.indigo600} weight="duotone" />
                </View>
                <View style={styles.settingTextContainer}>
                  <Text style={styles.settingLabel}>Diplôme</Text>
                  <Text style={styles.settingValue} numberOfLines={1}>
                    {profile?.diploma 
                      ? profile.section 
                        ? `${profile.diploma} ${profile.section}` 
                        : profile.diploma 
                      : 'Non défini'}
                  </Text>
                </View>
              </View>
              <TouchableOpacity
                style={styles.changeButton}
                onPress={() => setShowDiplomaModal(true)}
                disabled={isResettingDiploma}
              >
                {isResettingDiploma ? (
                  <ActivityIndicator size="small" color={colors.indigo600} />
                ) : (
                  <View style={styles.changeButtonContent}>
                    <ArrowsClockwise size={14} color={colors.slate700} weight="bold" />
                    <Text style={styles.changeButtonText}>Changer</Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>

            {/* Matières */}
            <View style={styles.settingCard}>
              <View style={styles.settingLeft}>
                <View style={[styles.settingIcon, { backgroundColor: colors.emerald100 }]}>
                  <BookOpenText size={20} color={colors.emerald500} weight="duotone" />
                </View>
                <View style={styles.settingTextContainer}>
                  <Text style={styles.settingLabel}>Matières</Text>
                  <Text style={styles.settingValue}>
                    {subjectCount > 0 ? `${subjectCount} matière${subjectCount > 1 ? 's' : ''}` : 'Aucune'}
                  </Text>
                </View>
              </View>
              <TouchableOpacity style={styles.changeButton} onPress={handleModifySubjects}>
                <View style={styles.changeButtonContent}>
                  <PencilSimple size={14} color={colors.slate700} weight="bold" />
                  <Text style={styles.changeButtonText}>Modifier</Text>
                </View>
              </TouchableOpacity>
            </View>
          </View>

          {/* ✅ Abonnement - Corrigé pour Android */}
          <View style={styles.section}>
            {subscribed ? (
              <LinearGradient
                colors={[colors.indigo50, colors.white]}
                style={styles.subscriptionCard}
              >
                <View style={styles.subscriptionTop}>
                  <View style={[styles.settingIcon, { backgroundColor: colors.amber100 }]}>
                    <Crown size={20} color={colors.amber500} weight="fill" />
                  </View>
                  <View style={styles.subscriptionInfo}>
                    {/* ✅ Changé "Abonnement" en "Abonné" - plus court */}
                    <Text style={styles.settingLabel} numberOfLines={1}>Abonné</Text>
                    <Text style={styles.premiumText}>Premium</Text>
                  </View>
                  <TouchableOpacity
                    style={styles.manageButton}
                    onPress={handleManageSubscription}
                    disabled={isPortalLoading}
                  >
                    {isPortalLoading ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <Text style={styles.manageButtonText}>Gérer</Text>
                    )}
                  </TouchableOpacity>
                </View>
                {subscriptionEnd && (
                  <View style={styles.subscriptionDate}>
                    <CalendarCheck size={16} color={colors.slate500} weight="duotone" />
                    <Text style={styles.subscriptionDateText}>
                      {cancelAtPeriodEnd
                        ? `Annulation prévue le ${formatDateFr(subscriptionEnd)}`
                        : `Renouvellement le ${formatDateFr(subscriptionEnd)}`}
                    </Text>
                  </View>
                )}
              </LinearGradient>
            ) : (
              <View style={styles.freeCard}>
                <View style={styles.subscriptionTop}>
                  <View style={[styles.settingIcon, { backgroundColor: colors.slate100 }]}>
                    <Crown size={20} color={colors.slate400} weight="duotone" />
                  </View>
                  <View style={styles.subscriptionInfo}>
                    {/* ✅ Changé "Abonnement" en "Compte" - plus court */}
                    <Text style={styles.settingLabel} numberOfLines={1}>Compte</Text>
                    <Text style={styles.freeText}>Gratuit</Text>
                  </View>
                  <TouchableOpacity style={styles.upgradeButton} onPress={handleUpgrade}>
                    <Star size={16} color={colors.white} weight="fill" />
                    <Text style={styles.upgradeButtonText}>Premium</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}
          </View>

          {/* Mes stats */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <ChartBar size={20} color={colors.amber500} weight="duotone" />
              <Text style={styles.sectionTitle}>Mes stats</Text>
            </View>

            <View style={styles.progressionCard}>
              {/* Niveau */}
              <View style={styles.levelHeader}>
                <Text style={styles.levelTitle}>Niveau {currentLevel}</Text>
                <View style={styles.levelBadge}>
                  <Text style={styles.levelBadgeText}>{getLevelName(currentLevel)}</Text>
                </View>
              </View>

              <View style={styles.xpRow}>
                <Text style={styles.xpText}>{xpInCurrentLevel} XP</Text>
                <Text style={styles.xpText}>{xpForNextLevel} XP</Text>
              </View>

              <ProgressBar progress={progress} color={colors.indigo600} style={styles.progressBar} />

              <Text style={styles.motivationText}>
                Encore {xpForNextLevel - xpInCurrentLevel} XP pour le niveau suivant
              </Text>

              {/* Stats */}
              <View style={styles.statsRow}>
                <View style={styles.statBadge}>
                  <View style={[styles.statCircle, { backgroundColor: colors.amber100 }]}>
                    <Fire size={24} color={colors.amber500} weight="fill" />
                  </View>
                  <Text style={styles.statNumber}>{profile?.streak_days || 0}</Text>
                  <Text style={styles.statLabel}>Jours de suite</Text>
                </View>

                <View style={styles.statBadge}>
                  <View style={[styles.statCircle, { backgroundColor: colors.emerald100 }]}>
                    <Trophy size={24} color={colors.emerald500} weight="duotone" />
                  </View>
                  <Text style={styles.statNumber}>{profile?.best_streak || 0}</Text>
                  <Text style={styles.statLabel}>Meilleur streak</Text>
                </View>

                <View style={styles.statBadge}>
                  <View style={[styles.statCircle, { backgroundColor: colors.indigo100 }]}>
                    <Star size={24} color={colors.indigo600} weight="fill" />
                  </View>
                  <Text style={styles.statNumber}>{profile?.xp || 0}</Text>
                  <Text style={styles.statLabel}>XP total</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Informations légales - Sans icône dans le titre */}
          <View style={styles.section}>
            <Text style={styles.sectionTitleSimple}>Informations légales</Text>

            <View style={styles.groupedCard}>
              <TouchableOpacity style={styles.groupedCardItem} onPress={handleOpenPrivacyPolicy}>
                <View style={styles.groupedCardLeft}>
                  <View style={[styles.settingIcon, { backgroundColor: colors.indigo100 }]}>
                    <ShieldCheck size={20} color={colors.indigo600} weight="duotone" />
                  </View>
                  <Text style={styles.groupedCardText}>Politique de confidentialité</Text>
                </View>
                <Text style={styles.groupedCardArrow}>›</Text>
              </TouchableOpacity>

              <View style={styles.groupedCardDivider} />

              <TouchableOpacity style={styles.groupedCardItem} onPress={handleOpenTerms}>
                <View style={styles.groupedCardLeft}>
                  <View style={[styles.settingIcon, { backgroundColor: colors.slate100 }]}>
                    <FileText size={20} color={colors.slate500} weight="duotone" />
                  </View>
                  <Text style={styles.groupedCardText}>Conditions d'utilisation</Text>
                </View>
                <Text style={styles.groupedCardArrow}>›</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Compte - Restaurer, Déconnexion et Suppression groupés */}
          <View style={styles.section}>
            <Text style={styles.sectionTitleSimple}>Compte</Text>

            <View style={styles.groupedCard}>
              {/* ✅ Restaurer mes achats */}
              <TouchableOpacity style={styles.groupedCardItem} onPress={handleRestorePurchases}>
                <View style={styles.groupedCardLeft}>
                  <View style={[styles.settingIcon, { backgroundColor: colors.amber100 }]}>
                    <ArrowsClockwise size={20} color={colors.amber500} weight="duotone" />
                  </View>
                  <Text style={styles.groupedCardText}>Restaurer mes achats</Text>
                </View>
                <Text style={styles.groupedCardArrow}>›</Text>
              </TouchableOpacity>

              <View style={styles.groupedCardDivider} />

              <TouchableOpacity style={styles.groupedCardItem} onPress={handleLogout}>
                <View style={styles.groupedCardLeft}>
                  <View style={[styles.settingIcon, { backgroundColor: colors.rose100 }]}>
                    <SignOut size={20} color={colors.rose500} weight="duotone" />
                  </View>
                  <Text style={styles.groupedCardTextLogout}>Se déconnecter</Text>
                </View>
                <Text style={styles.groupedCardArrow}>›</Text>
              </TouchableOpacity>

              <View style={styles.groupedCardDivider} />

              <TouchableOpacity style={styles.groupedCardItem} onPress={handleDeleteAccountPress}>
                <View style={styles.groupedCardLeft}>
                  <View style={[styles.settingIcon, { backgroundColor: colors.slate100 }]}>
                    <Trash size={20} color={colors.slate400} weight="duotone" />
                  </View>
                  <Text style={styles.groupedCardTextDelete}>Supprimer mon compte</Text>
                </View>
                <Text style={styles.groupedCardArrowMuted}>›</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Spacer bottom */}
          <View style={{ height: 100 }} />
        </View>
      </ScrollView>

      {/* Modal Confirmation Diplôme */}
      <Modal
        visible={showDiplomaModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDiplomaModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Changer de diplôme ?</Text>
            <Text style={styles.modalDescription}>
              Changer de diplôme réinitialisera tes matières et ta configuration de révision.
              {'\n\n'}
              Tes statistiques (XP, streak) et ton historique de révision seront conservés.
            </Text>
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setShowDiplomaModal(false)}
              >
                <Text style={styles.modalCancelText}>Annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalConfirmButton}
                onPress={handleChangeDiploma}
              >
                <Text style={styles.modalConfirmText}>Confirmer</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Modal 1 : Avertissement suppression */}
      <Modal
        visible={showDeleteModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDeleteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.deleteWarningIcon}>
              <Warning size={32} color={colors.rose500} weight="fill" />
            </View>
            <Text style={styles.modalTitle}>Supprimer ton compte ?</Text>
            <Text style={styles.modalDescription}>
              Cette action est <Text style={styles.boldText}>irréversible</Text>. Toutes tes données seront définitivement supprimées :
              {'\n\n'}
              • Tes flashcards et quiz{'\n'}
              • Ton historique de révision{'\n'}
              • Tes statistiques et XP{'\n'}
              • Tes conversations avec Ernest{'\n'}
              • Tes documents uploadés
            </Text>
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setShowDeleteModal(false)}
              >
                <Text style={styles.modalCancelText}>Annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalDeleteButton}
                onPress={handleDeleteAccountContinue}
              >
                <Text style={styles.modalDeleteText}>Continuer</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Modal 2 : Confirmation finale */}
      <Modal
        visible={showDeleteConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => {
          setShowDeleteConfirmModal(false);
          setDeleteConfirmText('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.deleteWarningIcon}>
              <Trash size={32} color={colors.rose500} weight="fill" />
            </View>
            <Text style={styles.modalTitle}>Confirmation finale</Text>
            <Text style={styles.modalDescription}>
              Pour confirmer la suppression de ton compte, tape{' '}
              <Text style={styles.boldText}>SUPPRIMER</Text> ci-dessous :
            </Text>
            
            <TextInput
              style={styles.deleteConfirmInput}
              placeholder="Tape SUPPRIMER"
              placeholderTextColor={colors.slate400}
              value={deleteConfirmText}
              onChangeText={setDeleteConfirmText}
              autoCapitalize="characters"
              mode="outlined"
              outlineColor={colors.slate200}
              activeOutlineColor={colors.rose500}
              textColor={colors.slate900}
            />

            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => {
                  setShowDeleteConfirmModal(false);
                  setDeleteConfirmText('');
                }}
              >
                <Text style={styles.modalCancelText}>Annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalDeleteButton,
                  deleteConfirmText !== 'SUPPRIMER' && styles.modalDeleteButtonDisabled,
                ]}
                onPress={handleDeleteAccountConfirm}
                disabled={deleteConfirmText !== 'SUPPRIMER' || isDeleting}
              >
                {isDeleting ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Text style={styles.modalDeleteText}>Supprimer</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    color: colors.slate500,
    fontSize: 15,
  },

  // ✅ Header Wrapper - Fond gris visible sur les côtés
  headerWrapper: {
    backgroundColor: colors.slate50,
    width: '100%',
    ...(isWeb && {
      alignItems: 'center',
    }),
  },
  // ✅ Header - Gradient indigo avec coins arrondis en bas
  header: {
    paddingTop: isWeb ? 24 : 64,
    paddingBottom: isWeb ? 24 : 32,
    paddingHorizontal: 20,
    borderBottomLeftRadius: 32,
    borderBottomRightRadius: 32,
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    ...(isWeb && {
      alignSelf: 'center',
    }),
  },
  headerInner: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  // ✅ Avatar en cercle blanc
  headerIconCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  headerAvatarText: {
    color: colors.indigo600,
    fontSize: 24,
    fontWeight: '700',
  },
  headerTextContainer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.white,
    letterSpacing: -0.5,
    marginBottom: 6,
  },
  headerSubtitle: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '500',
  },

  // ✅ ScrollView
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100,
    ...(isWeb && {
      alignItems: 'center',
    }),
  },

  // ✅ Inner container pour le contenu (centré sur web)
  innerContainer: {
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    alignSelf: 'center',
  },

  // Section standard
  section: {
    paddingHorizontal: 20,
    marginTop: 20,
    position: 'relative',
    zIndex: 1,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
  },
  sectionTitle: {
    color: colors.slate900,
    fontWeight: '700',
    fontSize: 17,
  },
  // Titre de section simple (sans icône)
  sectionTitleSimple: {
    color: colors.slate500,
    fontWeight: '600',
    fontSize: 14,
    marginBottom: 12,
    marginLeft: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // User Card
  userCard: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 16,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  userInfo: {
    gap: 4,
  },
  userName: {
    color: colors.slate900,
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 2,
  },
  userEmail: {
    color: colors.slate700,
    fontSize: 15,
    fontWeight: '500',
  },
  memberSince: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  memberSinceText: {
    color: colors.slate400,
    fontSize: 13,
  },

  // Settings Card
  settingCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  settingLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  settingIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    flexShrink: 0,
  },
  settingTextContainer: {
    flex: 1,
    minWidth: 0,
  },
  settingLabel: {
    color: colors.slate500,
    fontSize: 12,
  },
  settingValue: {
    color: colors.slate900,
    fontWeight: '600',
    fontSize: 15,
    marginTop: 2,
  },
  changeButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.slate200,
    backgroundColor: colors.white,
    marginLeft: 8,
    flexShrink: 0,
  },
  changeButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  changeButtonText: {
    color: colors.slate700,
    fontSize: 13,
    fontWeight: '600',
  },

  // Subscription Card
  subscriptionCard: {
    borderRadius: 20,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.indigo200,
  },
  freeCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 20,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  subscriptionTop: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscriptionInfo: {
    flex: 1,
    marginLeft: 14,
    minWidth: 0,
  },
  premiumText: {
    color: colors.indigo600,
    fontWeight: '700',
    fontSize: 16,
    marginTop: 2,
  },
  freeText: {
    color: colors.slate700,
    fontWeight: '600',
    fontSize: 15,
    marginTop: 2,
  },
  manageButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: colors.indigo600,
    marginLeft: 8,
    flexShrink: 0,
  },
  manageButtonText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
  },
  upgradeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: colors.amber500,
    marginLeft: 8,
    flexShrink: 0,
  },
  upgradeButtonText: {
    color: colors.white,
    fontSize: 13,
    fontWeight: '600',
  },
  subscriptionDate: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 14,
    paddingTop: 14,
    borderTopWidth: 1,
    borderTopColor: colors.indigo100,
  },
  subscriptionDateText: {
    color: colors.slate500,
    fontSize: 13,
  },

  // Progression Card
  progressionCard: {
    backgroundColor: colors.white,
    borderRadius: 20,
    padding: 20,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
    overflow: 'hidden',
    position: 'relative',
    zIndex: 1,
  },
  levelHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  levelTitle: {
    color: colors.slate900,
    fontWeight: '700',
    fontSize: 18,
  },
  levelBadge: {
    backgroundColor: colors.indigo600,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 10,
  },
  levelBadgeText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: '600',
  },
  xpRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  xpText: {
    color: colors.slate500,
    fontSize: 13,
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.slate200,
    marginBottom: 12,
  },
  motivationText: {
    color: colors.slate500,
    textAlign: 'center',
    marginBottom: 24,
    fontSize: 13,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.slate100,
  },
  statBadge: {
    alignItems: 'center',
    gap: 8,
  },
  statCircle: {
    width: 52,
    height: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  statNumber: {
    color: colors.slate900,
    fontWeight: '700',
    fontSize: 22,
  },
  statLabel: {
    color: colors.slate500,
    fontSize: 11,
    textAlign: 'center',
  },

  // Grouped Card (pour Legal et Compte)
  groupedCard: {
    backgroundColor: colors.white,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  groupedCardItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  groupedCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  groupedCardText: {
    color: colors.slate700,
    fontSize: 15,
    fontWeight: '500',
  },
  groupedCardTextLogout: {
    color: colors.rose500,
    fontSize: 15,
    fontWeight: '600',
  },
  groupedCardTextDelete: {
    color: colors.slate400,
    fontSize: 14,
    fontWeight: '500',
  },
  groupedCardArrow: {
    color: colors.slate400,
    fontSize: 22,
    fontWeight: '300',
  },
  groupedCardArrowMuted: {
    color: colors.slate300,
    fontSize: 22,
    fontWeight: '300',
  },
  groupedCardDivider: {
    height: 1,
    backgroundColor: colors.slate100,
    marginLeft: 74,
  },

  // Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    backgroundColor: colors.white,
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.slate900,
    marginBottom: 12,
    textAlign: 'center',
  },
  modalDescription: {
    fontSize: 14,
    color: colors.slate600,
    lineHeight: 22,
    marginBottom: 24,
  },
  boldText: {
    fontWeight: '700',
    color: colors.slate900,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: colors.slate100,
    alignItems: 'center',
  },
  modalCancelText: {
    color: colors.slate700,
    fontSize: 15,
    fontWeight: '600',
  },
  modalConfirmButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: colors.rose500,
    alignItems: 'center',
  },
  modalConfirmText: {
    color: colors.white,
    fontSize: 15,
    fontWeight: '600',
  },

  // Delete Modal Specific
  deleteWarningIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.rose100,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalDeleteButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: colors.rose500,
    alignItems: 'center',
  },
  modalDeleteButtonDisabled: {
    backgroundColor: colors.slate300,
  },
  modalDeleteText: {
    color: colors.white,
    fontSize: 15,
    fontWeight: '600',
  },
  deleteConfirmInput: {
    backgroundColor: colors.white,
    marginBottom: 20,
    fontSize: 16,
  },
});
